export type StructuralWriteToken = Readonly<{ id: symbol; projectId: string }>;

type VersionWriteOptions = {
  structuralWriteToken?: StructuralWriteToken;
};

type SegmentWriteOptions = {
  queueScope?: object;
  segmentIds?: string[];
};

type VersionWriteAdmission = {
  execute: () => Promise<void>;
  structuralWriteToken?: StructuralWriteToken;
};

type ProjectWriteState = {
  activeSegmentWrites: Set<Promise<boolean>>;
  activeStructuralWrite: null | StructuralWriteToken;
  isVersionWriteActive: boolean;
  pendingVersionWriteCount: number;
  segmentWriteTailsByScope: Map<object, Map<string, Promise<void>>>;
  stateChangeWaiters: Set<() => void>;
  versionWriteAdmissions: VersionWriteAdmission[];
  versionWritePendingListeners: Set<() => void>;
};

const projectWriteStates = new Map<string, ProjectWriteState>();
const defaultSegmentWriteQueueScope = {};

const createProjectWriteState = (): ProjectWriteState => ({
  activeSegmentWrites: new Set(),
  activeStructuralWrite: null,
  isVersionWriteActive: false,
  pendingVersionWriteCount: 0,
  segmentWriteTailsByScope: new Map(),
  stateChangeWaiters: new Set(),
  versionWriteAdmissions: [],
  versionWritePendingListeners: new Set(),
});

const getProjectWriteState = (projectId: string) => {
  const existingState = projectWriteStates.get(projectId);

  if (existingState) return existingState;

  const state = createProjectWriteState();
  projectWriteStates.set(projectId, state);
  return state;
};

const deleteProjectWriteStateIfIdle = (projectId: string, state: ProjectWriteState) => {
  if (
    !state.activeSegmentWrites.size &&
    !state.activeStructuralWrite &&
    !state.isVersionWriteActive &&
    state.pendingVersionWriteCount === 0 &&
    !state.segmentWriteTailsByScope.size &&
    !state.stateChangeWaiters.size &&
    !state.versionWriteAdmissions.length &&
    !state.versionWritePendingListeners.size
  ) {
    projectWriteStates.delete(projectId);
  }
};

const notifyStateChange = (state: ProjectWriteState) => {
  [...state.stateChangeWaiters].forEach((resolve) => resolve());
  state.stateChangeWaiters.clear();
};

const notifyVersionWritePendingChange = (state: ProjectWriteState) => {
  state.versionWritePendingListeners.forEach((listener) => listener());
};

const waitForStateChange = (state: ProjectWriteState) =>
  new Promise<void>((resolve) => {
    state.stateChangeWaiters.add(resolve);
  });

const waitForActiveSegmentWrites = async (state: ProjectWriteState) => {
  let allSucceeded = true;

  while (state.activeSegmentWrites.size) {
    const writes = [...state.activeSegmentWrites];
    // eslint-disable-next-line no-await-in-loop -- include writes joining before version admission
    const results = await Promise.all(writes);
    allSucceeded = results.every(Boolean) && allSucceeded;
  }

  return allSucceeded;
};

const getNextVersionWriteAdmissionIndex = (state: ProjectWriteState) => {
  if (!state.activeStructuralWrite) return state.versionWriteAdmissions.length ? 0 : -1;

  return state.versionWriteAdmissions.findIndex(
    ({ structuralWriteToken }) => structuralWriteToken === state.activeStructuralWrite,
  );
};

const runNextVersionWriteAdmission = (projectId: string, state: ProjectWriteState) => {
  if (state.isVersionWriteActive) return;

  const nextIndex = getNextVersionWriteAdmissionIndex(state);

  if (nextIndex === -1) return;

  const [admission] = state.versionWriteAdmissions.splice(nextIndex, 1);
  state.isVersionWriteActive = true;

  void admission.execute().finally(() => {
    state.isVersionWriteActive = false;
    state.pendingVersionWriteCount -= 1;
    notifyStateChange(state);
    notifyVersionWritePendingChange(state);
    runNextVersionWriteAdmission(projectId, state);
    deleteProjectWriteStateIfIdle(projectId, state);
  });
};

export const getIsTranscriptVersionWritePending = (projectId: string) =>
  (projectWriteStates.get(projectId)?.pendingVersionWriteCount || 0) > 0;

export const subscribeTranscriptVersionWritePending = (projectId: string, listener: () => void) => {
  const state = getProjectWriteState(projectId);
  state.versionWritePendingListeners.add(listener);

  return () => {
    state.versionWritePendingListeners.delete(listener);
    deleteProjectWriteStateIfIdle(projectId, state);
  };
};

export const tryAcquireStructuralWrite = (projectId: string): null | StructuralWriteToken => {
  const state = getProjectWriteState(projectId);

  if (state.activeStructuralWrite || state.pendingVersionWriteCount > 0) return null;

  const token = Object.freeze({ id: Symbol('structural-editor-write'), projectId });
  state.activeStructuralWrite = token;
  return token;
};

export const releaseStructuralWrite = (token: StructuralWriteToken) => {
  const state = projectWriteStates.get(token.projectId);

  if (!state || state.activeStructuralWrite !== token) return false;

  state.activeStructuralWrite = null;
  notifyStateChange(state);
  runNextVersionWriteAdmission(token.projectId, state);
  deleteProjectWriteStateIfIdle(token.projectId, state);
  return true;
};

export const runCoordinatedSegmentWrite = async (
  projectId: string,
  write: () => Promise<boolean>,
  { queueScope = defaultSegmentWriteQueueScope, segmentIds = [] }: SegmentWriteOptions = {},
): Promise<boolean> => {
  const state = getProjectWriteState(projectId);

  while (state.isVersionWriteActive) {
    // eslint-disable-next-line no-await-in-loop -- consecutive version admissions remain exclusive
    await waitForStateChange(state);
  }

  const uniqueSegmentIds = [...new Set(segmentIds)];
  const segmentWriteTails = state.segmentWriteTailsByScope.get(queueScope) || new Map<string, Promise<void>>();
  state.segmentWriteTailsByScope.set(queueScope, segmentWriteTails);
  const precedingWrites = uniqueSegmentIds.flatMap((segmentId) => {
    const tail = segmentWriteTails.get(segmentId);
    return tail ? [tail] : [];
  });
  let releaseSegmentIds!: () => void;
  const segmentTail = new Promise<void>((resolve) => {
    releaseSegmentIds = resolve;
  });
  uniqueSegmentIds.forEach((segmentId) => segmentWriteTails.set(segmentId, segmentTail));
  let writePromise: Promise<boolean>;

  try {
    writePromise = precedingWrites.length ? Promise.all(precedingWrites).then(write) : write();
  } catch (error) {
    writePromise = Promise.reject(error);
  }
  state.activeSegmentWrites.add(writePromise);

  try {
    return await writePromise;
  } finally {
    state.activeSegmentWrites.delete(writePromise);
    uniqueSegmentIds.forEach((segmentId) => {
      if (segmentWriteTails.get(segmentId) === segmentTail) {
        segmentWriteTails.delete(segmentId);
      }
    });
    if (!segmentWriteTails.size) state.segmentWriteTailsByScope.delete(queueScope);
    releaseSegmentIds();
    notifyStateChange(state);
    deleteProjectWriteStateIfIdle(projectId, state);
  }
};

export const waitForCoordinatedSegmentWrites = (projectId: string) => {
  const state = projectWriteStates.get(projectId);
  return state ? waitForActiveSegmentWrites(state) : Promise.resolve(true);
};

/**
 * Gives transcript-version work exclusive admission against version writes, structural editor
 * operations, and coordinator-tracked segment PATCHes belonging to the same project. A structural
 * owner may pass its exact capability to run recovery snapshot work without waiting on itself.
 */
export const withTranscriptVersionWriteLock = <T>(
  projectId: string,
  write: () => Promise<T>,
  { structuralWriteToken }: VersionWriteOptions = {},
): Promise<T> => {
  const state = getProjectWriteState(projectId);
  state.pendingVersionWriteCount += 1;
  notifyVersionWritePendingChange(state);

  const result = new Promise<T>((resolve, reject) => {
    state.versionWriteAdmissions.push({
      execute: async () => {
        try {
          const segmentWritesSucceeded = await waitForActiveSegmentWrites(state);

          if (!segmentWritesSucceeded) {
            throw new Error('A pending segment write failed before the transcript version was created');
          }

          resolve(await write());
        } catch (error) {
          reject(error);
        }
      },
      structuralWriteToken: structuralWriteToken?.projectId === projectId ? structuralWriteToken : undefined,
    });
  });

  runNextVersionWriteAdmission(projectId, state);
  return result;
};
